import React, { useState, useEffect } from 'react';
import {
  getCostCenters, createCostCenter, updateCostCenter, deleteCostCenter,
  getCustomers, createCustomer, updateCustomer, deleteCustomer,
  getJobTypes, createJobType, updateJobType, deleteJobType,
  getJobIds, createJobId, updateJobId, deleteJobId,
  getEmployees, createEmployee, updateEmployee, deleteEmployee, resetEmployeePassword,
  getEmployeeSalaries, updateEmployeeSalary, getVacationBalance,
  getAdminTimesheetLines, addAdminTimesheetLine, deleteAdminTimesheetLine, updateAdminTimesheetEntry,
  getHolidays, createHolidayEntry, updateHolidayEntry, deleteHolidayEntry, syncHolidaysForYear,
  getAdminReferenceData, getTimesheetSummary, toggleMonthStatus, exportMonthReport,
  getBackupData, getReferenceData
} from '../api';

function AdminPage({ user, onLogout, onBack }) {
  const [activeTab, setActiveTab] = useState('employees');
  const [costCenters, setCostCenters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [jobIds, setJobIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [refData, setRefData] = useState({ costCenters: [], customers: [], jobTypes: [] });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [cc, cust, jt, ji, emp, ref] = await Promise.all([
        getCostCenters(), getCustomers(), getJobTypes(), getJobIds(), getEmployees(), getAdminReferenceData()
      ]);
      setCostCenters(cc);
      setCustomers(cust);
      setJobTypes(jt);
      setJobIds(ji);
      setEmployees(emp);
      setRefData(ref);
    } catch (err) {
      setError(err.message);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 3000);
  };

  return (
    <div className="admin-container">
      <header className="app-header">
        <div className="header-left">
          <h1>Admin</h1>
          <button onClick={onBack} className="btn-secondary">← Back to Timesheet</button>
        </div>
        <div className="header-right">
          <span className="user-name">{user.full_name}</span>
          <button onClick={onLogout} className="btn-logout">Logout</button>
        </div>
      </header>

      {error && <div className="error-banner" role="alert">{error}</div>}
      {success && <div className="success-banner" role="status">{success}</div>}

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveTab('employees')}
        >
          Employees
        </button>
        <button
          className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Timesheet Summary
        </button>
        <button
          className={`tab-btn ${activeTab === 'editTimesheet' ? 'active' : ''}`}
          onClick={() => setActiveTab('editTimesheet')}
        >
          Edit Timesheets
        </button>
        <button
          className={`tab-btn ${activeTab === 'vacationBalance' ? 'active' : ''}`}
          onClick={() => setActiveTab('vacationBalance')}
        >
          Vacation Balance
        </button>
        <button
          className={`tab-btn ${activeTab === 'costCenters' ? 'active' : ''}`}
          onClick={() => setActiveTab('costCenters')}
        >
          Cost Centers
        </button>
        <button
          className={`tab-btn ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Customers
        </button>
        <button
          className={`tab-btn ${activeTab === 'jobTypes' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobTypes')}
        >
          Job Types
        </button>
        <button
          className={`tab-btn ${activeTab === 'jobIds' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobIds')}
        >
          Job IDs
        </button>
        <button
          className={`tab-btn ${activeTab === 'holidays' ? 'active' : ''}`}
          onClick={() => setActiveTab('holidays')}
        >
          Holidays
        </button>
        <button
          className={`tab-btn ${activeTab === 'backup' ? 'active' : ''}`}
          onClick={() => setActiveTab('backup')}
        >
          Backup
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'employees' && (
          <EmployeesTab
            items={employees}
            setItems={setEmployees}
            currentUserId={user.id}
            showMessage={showMessage}
          />
        )}
        {activeTab === 'summary' && (
          <SummaryTab employees={employees} showMessage={showMessage} />
        )}
        {activeTab === 'editTimesheet' && (
          <EditTimesheetTab employees={employees} showMessage={showMessage} />
        )}
        {activeTab === 'vacationBalance' && (
          <VacationBalanceTab employees={employees} showMessage={showMessage} />
        )}
        {activeTab === 'costCenters' && (
          <CostCentersTab
            items={costCenters}
            setItems={setCostCenters}
            showMessage={showMessage}
          />
        )}
        {activeTab === 'customers' && (
          <CustomersTab
            items={customers}
            setItems={setCustomers}
            costCenters={costCenters}
            showMessage={showMessage}
          />
        )}
        {activeTab === 'jobTypes' && (
          <JobTypesTab
            items={jobTypes}
            setItems={setJobTypes}
            showMessage={showMessage}
          />
        )}
        {activeTab === 'jobIds' && (
          <JobIdsTab
            items={jobIds}
            setItems={setJobIds}
            refData={refData}
            showMessage={showMessage}
          />
        )}
        {activeTab === 'holidays' && (
          <HolidaysTab showMessage={showMessage} />
        )}
        {activeTab === 'backup' && (
          <BackupTab showMessage={showMessage} />
        )}
      </div>
    </div>
  );
}

// ============ Employees Tab ============
function EmployeesTab({ items, setItems, currentUserId, showMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    employee_id: '', username: '', password: '', full_name: '', email: '', role: 'user', report_prefix: '', salary: '', salary_type: 'salary', start_date: '', vacation_days_per_year: '20', active: 1
  });

  const resetForm = () => {
    setForm({ employee_id: '', username: '', password: '', full_name: '', email: '', role: 'user', report_prefix: '', salary: '', salary_type: 'salary', start_date: '', vacation_days_per_year: '20', active: 1 });
    setEditingId(null);
  };

  const handleSave = async () => {
    if (editingId) {
      if (!form.full_name) return showMessage('Full name is required', true);
      try {
        const updated = await updateEmployee(editingId, {
          employee_id: form.employee_id,
          full_name: form.full_name,
          email: form.email,
          role: form.role,
          report_prefix: form.report_prefix,
          salary: parseFloat(form.salary) || 0,
          salary_type: form.salary_type,
          start_date: form.start_date,
          vacation_days_per_year: parseFloat(form.vacation_days_per_year) || 20,
          active: form.active
        });
        setItems(items.map(i => i.id === editingId ? updated : i));
        showMessage('Employee updated');
        resetForm();
      } catch (err) { showMessage(err.message, true); }
    } else {
      if (!form.username || !form.password || !form.full_name) return showMessage('Username, password, and full name are required', true);
      try {
        const created = await createEmployee({
          employee_id: form.employee_id,
          username: form.username,
          password: form.password,
          full_name: form.full_name,
          email: form.email,
          role: form.role,
          report_prefix: form.report_prefix,
          salary: parseFloat(form.salary) || 0,
          salary_type: form.salary_type,
          start_date: form.start_date,
          vacation_days_per_year: parseFloat(form.vacation_days_per_year) || 20,
          active: form.active
        });
        setItems([...items, created]);
        showMessage('Employee created');
        resetForm();
      } catch (err) { showMessage(err.message, true); }
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      employee_id: item.employee_id || '',
      username: item.username,
      password: '',
      full_name: item.full_name,
      email: item.email || '',
      role: item.role,
      report_prefix: item.report_prefix || '',
      salary: item.salary?.toString() || '0',
      salary_type: item.salary_type || 'salary',
      start_date: item.start_date || '',
      vacation_days_per_year: item.vacation_days_per_year?.toString() || '20',
      active: item.active !== undefined ? item.active : 1
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try {
      // First call without confirm to get record counts
      const info = await deleteEmployee(id, false);
      if (info.requiresConfirmation) {
        const r = info.recordsToDelete;
        const msg = `This will permanently delete ${info.employee} and all associated data:\n\n` +
          `• ${r.timesheetLines} timesheet line(s)\n` +
          `• ${r.timesheetEntries} timesheet entry/entries\n` +
          `• ${r.vacations} vacation record(s)\n` +
          `• ${r.timesheetStatuses} month status(es)\n\n` +
          `Are you sure you want to proceed?`;
        if (!window.confirm(msg)) return;
      }
      // Confirmed - delete with all records
      await deleteEmployee(id, true);
      setItems(items.filter(i => i.id !== id));
      showMessage('Employee and all associated records deleted');
    } catch (err) { showMessage(err.message, true); }
  };

  const handleResetPassword = async (id, username) => {
    const newPassword = window.prompt(`Enter new password for ${username} (min 6 characters):`);
    if (!newPassword) return;
    if (newPassword.length < 6) { showMessage('Password must be at least 6 characters', true); return; }
    try {
      await resetEmployeePassword(id, newPassword);
      showMessage(`Password reset for ${username}`);
    } catch (err) { showMessage(err.message, true); }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>{editingId ? 'Edit Employee' : 'Add Employee'}</h3>
        <div className="form-row">
          {!editingId && (
            <>
              <input
                placeholder="Username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
              <input
                placeholder="Password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </>
          )}
          <input
            placeholder="Employee ID"
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            style={{ width: '100px' }}
          />
          <input
            placeholder="Full Name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
          <input
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <input
            placeholder="Report Prefix"
            value={form.report_prefix}
            onChange={(e) => setForm({ ...form, report_prefix: e.target.value })}
            style={{ width: '100px' }}
          />
          <input
            placeholder="Salary"
            type="number"
            step="0.01"
            value={form.salary}
            onChange={(e) => setForm({ ...form, salary: e.target.value })}
            style={{ width: '100px' }}
          />
          <select value={form.salary_type} onChange={(e) => setForm({ ...form, salary_type: e.target.value })}>
            <option value="salary">Salary</option>
            <option value="hourly">Hourly</option>
          </select>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            title="Start Date"
          />
          <input
            placeholder="Vacation Days"
            type="number"
            step="0.5"
            value={form.vacation_days_per_year}
            onChange={(e) => setForm({ ...form, vacation_days_per_year: e.target.value })}
            style={{ width: '120px' }}
          />
          <select value={form.active} onChange={(e) => setForm({ ...form, active: parseInt(e.target.value) })}>
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
          <button onClick={handleSave} className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="btn-secondary">Cancel</button>}
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Emp ID</th><th>Username</th><th>Full Name</th><th>Email</th><th>Role</th><th>Report Prefix</th><th>Salary</th><th>Salary Type</th><th>Start Date</th><th>Vacation Days</th><th>Active</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {[...items].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(item => (
            <tr key={item.id} className={!item.active ? 'inactive-row' : ''}>
              <td>{item.employee_id || '—'}</td>
              <td>{item.username}</td>
              <td>{item.full_name}</td>
              <td>{item.email || '—'}</td>
              <td><span className={`role-badge role-${item.role}`}>{item.role}</span></td>
              <td>{item.report_prefix || '—'}</td>
              <td>{item.salary ? `$${Number(item.salary).toLocaleString()}` : '—'}</td>
              <td>{item.salary_type || 'salary'}</td>
              <td>{item.start_date || '—'}</td>
              <td>{item.vacation_days_per_year}</td>
              <td>{item.active ? 'Yes' : 'No'}</td>
              <td>
                <button onClick={() => handleEdit(item)} className="btn-edit">Edit</button>
                <button onClick={() => handleResetPassword(item.id, item.username)} className="btn-secondary" style={{ marginLeft: '0.25rem', padding: '0.3rem 0.5rem', fontSize: '0.75rem' }}>Reset Pwd</button>
                {item.id !== currentUserId && (
                  <button onClick={() => handleDelete(item.id)} className="btn-delete-admin">Delete</button>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="12" className="empty-cell">No employees</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============ Timesheet Summary Tab ============
function SummaryTab({ employees, showMessage }) {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const handleLoadSummary = async () => {
    if (!selectedEmployee) return showMessage('Please select an employee', true);
    setLoading(true);
    setReportData(null);
    try {
      const data = await getTimesheetSummary(selectedEmployee, selectedYear);
      setSummaryData(data);
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (month, currentStatus) => {
    const newStatus = currentStatus === 'closed' ? 'open' : 'closed';
    try {
      await toggleMonthStatus(parseInt(selectedEmployee), month, selectedYear, newStatus);
      setSummaryData(prev => ({
        ...prev,
        months: prev.months.map(m => m.month === month ? { ...m, status: newStatus } : m)
      }));
      showMessage(`${monthNames[month - 1]} ${newStatus === 'closed' ? 'closed' : 'reopened'}`);
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleExportReport = async (month) => {
    try {
      const data = await exportMonthReport(selectedEmployee, selectedYear, month);
      setReportData(data);
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleSalaryChange = async (month, value) => {
    const salary = parseFloat(value) || 0;
    try {
      await updateEmployeeSalary(parseInt(selectedEmployee), month, selectedYear, salary);
      // Update local state
      setSummaryData(prev => ({
        ...prev,
        months: prev.months.map(m => m.month === month ? { ...m, monthlySalary: salary } : m),
        totals: {
          ...prev.totals,
          totalSalary: prev.months.reduce((s, m) => s + (m.month === month ? salary : m.monthlySalary), 0)
        }
      }));
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleDownloadCSV = () => {
    if (!reportData) return;
    const rows = reportData.reportLines.map(line => [
      line.date,
      line.sage_id,
      line.nrows,
      line.gl_code,
      `"${line.description}"`,
      line.salary.toFixed(2),
      line.job_id_code
    ]);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${reportData.employee.report_prefix}_${reportData.year}_${String(reportData.month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="admin-form">
        <h3>Timesheet Summary</h3>
        <div className="form-row">
          <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
            <option value="">Select Employee</option>
            {employees.filter(emp => emp.active).map(emp => (
              <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.username})</option>
            ))}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {[2023, 2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={handleLoadSummary} className="btn-primary" disabled={!selectedEmployee || loading}>
            {loading ? 'Loading...' : 'Load Summary'}
          </button>
        </div>
      </div>

      {summaryData && (
        <div className="summary-results">
          <div className="summary-header">
            <h3>{summaryData.employee.full_name} — {summaryData.year}</h3>
            <div className="summary-meta">
              <span>Base Annual Salary: <strong>${Number(summaryData.employee.salary).toLocaleString()}</strong></span>
              <span>Vacation Allowance: <strong>{summaryData.employee.vacation_days_per_year} days (at start)</strong></span>
              <span>Carry Over: <strong>{summaryData.vacationCarryOver} days</strong></span>
            </div>
          </div>
          <table className="admin-table summary-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Status</th>
                <th>Working Days</th>
                <th>Total Work Hours</th>
                <th>Hours Reported</th>
                <th>Utilization</th>
                <th>Vacations Taken [Hours]</th>
                <th>Vac Balance [Days]</th>
                <th>Holiday Hours</th>
                <th>Monthly Salary</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {summaryData.months.map(m => {
                const utilization = m.totalWorkHours > 0 ? ((m.hoursWorked / m.totalWorkHours) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={m.month}>
                    <td>{monthNames[m.month - 1]}</td>
                    <td>
                      <span className={`month-status-badge ${m.status}`}>
                        {m.status === 'closed' ? 'Closed' : 'Open'}
                      </span>
                    </td>
                    <td>{m.workingDays}</td>
                    <td>{m.totalWorkHours}</td>
                    <td>{m.hoursWorked}</td>
                    <td>
                      <span className={`utilization ${parseFloat(utilization) >= 80 ? 'good' : parseFloat(utilization) >= 50 ? 'fair' : 'low'}`}>
                        {utilization}%
                      </span>
                    </td>
                    <td>{m.vacationHours || '—'}</td>
                    <td className={m.vacBalanceEnd < 0 ? 'negative' : ''}>{m.vacBalanceEnd !== undefined ? m.vacBalanceEnd.toFixed(1) : '—'}</td>
                    <td>{m.holidayHours || '—'}</td>
                    <td className="salary-cell">
                      <input
                        type="number"
                        step="0.01"
                        value={m.monthlySalary !== undefined ? m.monthlySalary : ''}
                        onChange={(e) => handleSalaryChange(m.month, e.target.value)}
                        className="salary-input"
                      />
                    </td>
                    <td className="action-cell">
                      <button
                        onClick={() => handleToggleStatus(m.month, m.status)}
                        className={m.status === 'closed' ? 'btn-edit' : 'btn-close-month-admin'}
                      >
                        {m.status === 'closed' ? 'Reopen' : 'Close'}
                      </button>
                      {m.status === 'closed' && (
                        <button onClick={() => handleExportReport(m.month)} className="btn-export">
                          Export
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td></td>
                <td><strong>{summaryData.months.reduce((s, m) => s + m.workingDays, 0)}</strong></td>
                <td><strong>{summaryData.totals.totalWorkHours}</strong></td>
                <td><strong>{summaryData.totals.hoursWorked}</strong></td>
                <td>
                  <strong>
                    {summaryData.totals.totalWorkHours > 0
                      ? ((summaryData.totals.hoursWorked / summaryData.totals.totalWorkHours) * 100).toFixed(1)
                      : '0.0'}%
                  </strong>
                </td>
                <td><strong>{summaryData.totals.vacationHours || '—'}</strong></td>
                <td><strong>{summaryData.months[11] ? summaryData.months[11].vacBalanceEnd.toFixed(1) : '—'}</strong></td>
                <td><strong>{summaryData.totals.holidayHours || '—'}</strong></td>
                <td><strong>${summaryData.totals.totalSalary ? summaryData.totals.totalSalary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Export Report Preview */}
      {reportData && (
        <div className="report-preview">
          <div className="report-header">
            <h3>Export Report — {reportData.employee.full_name} — {monthNames[reportData.month - 1]} {reportData.year}</h3>
            <button onClick={handleDownloadCSV} className="btn-primary" style={{ width: 'auto' }}>
              Download CSV
            </button>
          </div>
          <table className="admin-table report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Sage ID</th>
                <th>NRows</th>
                <th>GL Code</th>
                <th>Description</th>
                <th>Salary</th>
                <th>Job ID</th>
              </tr>
            </thead>
            <tbody>
              {reportData.reportLines.map((line, idx) => (
                <tr key={idx} className={idx === 0 ? 'report-total-line' : ''}>
                  <td>{line.date}</td>
                  <td>{line.sage_id}</td>
                  <td>{line.nrows}</td>
                  <td>{line.gl_code}</td>
                  <td>{line.description}</td>
                  <td className={line.salary < 0 ? 'negative' : ''}>
                    ${line.salary.toFixed(2)}
                  </td>
                  <td>{line.job_id_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ Cost Centers Tab ============
function CostCentersTab({ items, setItems, showMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ code: '', name: '' });

  const resetForm = () => { setForm({ code: '', name: '' }); setEditingId(null); };

  const handleSave = async () => {
    if (!form.code || !form.name) return showMessage('Code and name are required', true);
    try {
      if (editingId) {
        const updated = await updateCostCenter(editingId, form);
        setItems(items.map(i => i.id === editingId ? updated : i));
        showMessage('Cost center updated');
      } else {
        const created = await createCostCenter(form);
        setItems([...items, created]);
        showMessage('Cost center created');
      }
      resetForm();
    } catch (err) { showMessage(err.message, true); }
  };

  const handleEdit = (item) => { setEditingId(item.id); setForm({ code: item.code, name: item.name }); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cost center?')) return;
    try {
      await deleteCostCenter(id);
      setItems(items.filter(i => i.id !== id));
      showMessage('Cost center deleted');
    } catch (err) { showMessage(err.message, true); }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>{editingId ? 'Edit Cost Center' : 'Add Cost Center'}</h3>
        <div className="form-row">
          <input
            placeholder="Code (e.g. CC400)"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
          <input
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <button onClick={handleSave} className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="btn-secondary">Cancel</button>}
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr><th>Code</th><th>Name</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td>{item.code}</td>
              <td>{item.name}</td>
              <td>
                <button onClick={() => handleEdit(item)} className="btn-edit">Edit</button>
                <button onClick={() => handleDelete(item.id)} className="btn-delete-admin">Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="3" className="empty-cell">No cost centers</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============ Customers Tab ============
function CustomersTab({ items, setItems, costCenters, showMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ customer_acronym: '', customer_name: '', internal: 'yes', cost_center_id: '' });

  const resetForm = () => { setForm({ customer_acronym: '', customer_name: '', internal: 'yes', cost_center_id: '' }); setEditingId(null); };

  const handleSave = async () => {
    if (!form.customer_acronym || !form.customer_name) return showMessage('Acronym and name are required', true);
    const payload = { ...form, cost_center_id: form.cost_center_id ? parseInt(form.cost_center_id) : null };
    try {
      if (editingId) {
        const updated = await updateCustomer(editingId, payload);
        setItems(items.map(i => i.id === editingId ? updated : i));
        showMessage('Customer updated');
      } else {
        const created = await createCustomer(payload);
        setItems([...items, created]);
        showMessage('Customer created');
      }
      resetForm();
    } catch (err) { showMessage(err.message, true); }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      customer_acronym: item.customer_acronym,
      customer_name: item.customer_name,
      internal: item.internal || 'yes',
      cost_center_id: item.cost_center_id || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await deleteCustomer(id);
      setItems(items.filter(i => i.id !== id));
      showMessage('Customer deleted');
    } catch (err) { showMessage(err.message, true); }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>{editingId ? 'Edit Customer' : 'Add Customer'}</h3>
        <div className="form-row">
          <input
            placeholder="Acronym (e.g. ACME)"
            value={form.customer_acronym}
            onChange={(e) => setForm({ ...form, customer_acronym: e.target.value })}
          />
          <input
            placeholder="Customer Name"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          />
          <select value={form.internal} onChange={(e) => setForm({ ...form, internal: e.target.value })}>
            <option value="yes">Internal: Yes</option>
            <option value="no">Internal: No</option>
          </select>
          <select value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}>
            <option value="">No Cost Center</option>
            {costCenters.map(cc => (
              <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
            ))}
          </select>
          <button onClick={handleSave} className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="btn-secondary">Cancel</button>}
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr><th>Acronym</th><th>Name</th><th>Internal</th><th>Cost Center</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {[...items].sort((a, b) => a.customer_name.localeCompare(b.customer_name)).map(item => (
            <tr key={item.id}>
              <td>{item.customer_acronym}</td>
              <td>{item.customer_name}</td>
              <td>{item.internal || 'yes'}</td>
              <td>{item.cost_center_code ? `${item.cost_center_code} - ${item.cost_center_name}` : '—'}</td>
              <td>
                <button onClick={() => handleEdit(item)} className="btn-edit">Edit</button>
                <button onClick={() => handleDelete(item.id)} className="btn-delete-admin">Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="5" className="empty-cell">No customers</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============ Job Types Tab ============
function JobTypesTab({ items, setItems, showMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ type_id: '', type_description: '', gl_account: '', internal: 'yes', product: '', detailed_description: '' });

  const resetForm = () => { setForm({ type_id: '', type_description: '', gl_account: '', internal: 'yes', product: '', detailed_description: '' }); setEditingId(null); };

  const handleSave = async () => {
    if (!form.type_id || !form.type_description) return showMessage('Type ID and description are required', true);
    try {
      if (editingId) {
        const updated = await updateJobType(editingId, form);
        setItems(items.map(i => i.id === editingId ? updated : i));
        showMessage('Job type updated');
      } else {
        const created = await createJobType(form);
        setItems([...items, created]);
        showMessage('Job type created');
      }
      resetForm();
    } catch (err) { showMessage(err.message, true); }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      type_id: item.type_id,
      type_description: item.type_description,
      gl_account: item.gl_account || '',
      internal: item.internal || 'yes',
      product: item.product || '',
      detailed_description: item.detailed_description || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job type?')) return;
    try {
      await deleteJobType(id);
      setItems(items.filter(i => i.id !== id));
      showMessage('Job type deleted');
    } catch (err) { showMessage(err.message, true); }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>{editingId ? 'Edit Job Type' : 'Add Job Type'}</h3>
        <div className="form-row">
          <input
            placeholder="Type ID (e.g. DESIGN)"
            value={form.type_id}
            onChange={(e) => setForm({ ...form, type_id: e.target.value })}
          />
          <input
            placeholder="Type Description"
            value={form.type_description}
            onChange={(e) => setForm({ ...form, type_description: e.target.value })}
          />
          <input
            placeholder="GL Account (e.g. 5.1.001)"
            value={form.gl_account}
            onChange={(e) => setForm({ ...form, gl_account: e.target.value })}
          />
          <select value={form.internal} onChange={(e) => setForm({ ...form, internal: e.target.value })}>
            <option value="yes">Internal: Yes</option>
            <option value="no">Internal: No</option>
          </select>
          <input
            placeholder="Product"
            value={form.product}
            onChange={(e) => setForm({ ...form, product: e.target.value })}
          />
          <input
            placeholder="Detailed Description"
            value={form.detailed_description}
            onChange={(e) => setForm({ ...form, detailed_description: e.target.value })}
          />
          <button onClick={handleSave} className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="btn-secondary">Cancel</button>}
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr><th>Type ID</th><th>Description</th><th>GL Account</th><th>Internal</th><th>Product</th><th>Detailed Description</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {[...items].sort((a, b) => a.type_id.localeCompare(b.type_id)).map(item => (
            <tr key={item.id}>
              <td>{item.type_id}</td>
              <td>{item.type_description}</td>
              <td>{item.gl_account || '—'}</td>
              <td>{item.internal || 'yes'}</td>
              <td>{item.product || '—'}</td>
              <td>{item.detailed_description || '—'}</td>
              <td>
                <button onClick={() => handleEdit(item)} className="btn-edit">Edit</button>
                <button onClick={() => handleDelete(item.id)} className="btn-delete-admin">Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="7" className="empty-cell">No job types</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============ Job IDs Tab ============
function JobIdsTab({ items, setItems, refData, showMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [sortField, setSortField] = useState('job_id_code');
  const [sortDir, setSortDir] = useState('asc');
  const [form, setForm] = useState({
    job_id_code: '', description: '', cost_center_id: '', customer_id: '', job_type_id: ''
  });

  const resetForm = () => {
    setForm({ job_id_code: '', description: '', cost_center_id: '', customer_id: '', job_type_id: '' });
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.job_id_code || !form.description) return showMessage('Job ID code and description are required', true);
    const payload = {
      ...form,
      cost_center_id: form.cost_center_id ? parseInt(form.cost_center_id) : null,
      customer_id: form.customer_id ? parseInt(form.customer_id) : null,
      job_type_id: form.job_type_id ? parseInt(form.job_type_id) : null
    };
    try {
      if (editingId) {
        const updated = await updateJobId(editingId, payload);
        setItems(items.map(i => i.id === editingId ? updated : i));
        showMessage('Job ID updated');
      } else {
        const created = await createJobId(payload);
        setItems([...items, created]);
        showMessage('Job ID created');
      }
      resetForm();
    } catch (err) { showMessage(err.message, true); }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      job_id_code: item.job_id_code,
      description: item.description,
      cost_center_id: item.cost_center_id || '',
      customer_id: item.customer_id || '',
      job_type_id: item.job_type_id || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job ID?')) return;
    try {
      await deleteJobId(id);
      setItems(items.filter(i => i.id !== id));
      showMessage('Job ID deleted');
    } catch (err) { showMessage(err.message, true); }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>{editingId ? 'Edit Job ID' : 'Add Job ID'}</h3>
        <div className="form-row">
          <input
            placeholder="Job ID Code (e.g. JOB-006)"
            value={form.job_id_code}
            onChange={(e) => setForm({ ...form, job_id_code: e.target.value })}
          />
          <input
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <select value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}>
            <option value="">No Cost Center</option>
            {refData.costCenters.map(cc => (
              <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
            ))}
          </select>
          <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">No Customer</option>
            {refData.customers.map(c => (
              <option key={c.id} value={c.id}>{c.customer_acronym} - {c.customer_name}</option>
            ))}
          </select>
          <select value={form.job_type_id} onChange={(e) => setForm({ ...form, job_type_id: e.target.value })}>
            <option value="">No Job Type</option>
            {refData.jobTypes.map(jt => (
              <option key={jt.id} value={jt.id}>{jt.type_id} - {jt.type_description}</option>
            ))}
          </select>
          <button onClick={handleSave} className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="btn-secondary">Cancel</button>}
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            {[
              { label: 'Code',        field: 'job_id_code' },
              { label: 'Description', field: 'description' },
              { label: 'Cost Center', field: 'cost_center_name' },
              { label: 'Customer',    field: 'customer_name' },
            ].map(col => (
              <th
                key={col.field}
                onClick={() => {
                  if (sortField === col.field) {
                    setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortField(col.field);
                    setSortDir('asc');
                  }
                }}
                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
              >
                {col.label}
                {sortField === col.field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
              </th>
            ))}
            <th>Type</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {[...items].sort((a, b) => {
            const valA = (a[sortField] || '').toString().toLowerCase();
            const valB = (b[sortField] || '').toString().toLowerCase();
            return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          }).map(item => (
            <tr key={item.id}>
              <td>{item.job_id_code}</td>
              <td>{item.description}</td>
              <td>{item.cost_center_code ? `${item.cost_center_code} - ${item.cost_center_name}` : '—'}</td>
              <td>{item.customer_code ? `${item.customer_code} - ${item.customer_name}` : '—'}</td>
              <td>{item.job_type_code ? `${item.job_type_code} - ${item.job_type_name}` : '—'}</td>
              <td>
                <button onClick={() => handleEdit(item)} className="btn-edit">Edit</button>
                <button onClick={() => handleDelete(item.id)} className="btn-delete-admin">Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="6" className="empty-cell">No job IDs</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============ Holidays Tab ============
function HolidaysTab({ showMessage }) {
  const [holidays, setHolidays] = useState([]);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ holiday_date: '', holiday_name: '' });

  useEffect(() => {
    loadHolidays();
  }, [filterYear]);

  const loadHolidays = async () => {
    try {
      const data = await getHolidays(filterYear);
      setHolidays(data);
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const resetForm = () => { setForm({ holiday_date: '', holiday_name: '' }); setEditingId(null); };

  const handleSave = async () => {
    if (!form.holiday_date || !form.holiday_name) return showMessage('Date and name are required', true);
    try {
      if (editingId) {
        const updated = await updateHolidayEntry(editingId, form);
        setHolidays(holidays.map(h => h.id === editingId ? updated : h));
        showMessage('Holiday updated');
      } else {
        const created = await createHolidayEntry(form);
        setHolidays([...holidays, created].sort((a, b) => a.holiday_date.localeCompare(b.holiday_date)));
        showMessage('Holiday created');
      }
      resetForm();
    } catch (err) { showMessage(err.message, true); }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({ holiday_date: item.holiday_date, holiday_name: item.holiday_name });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await deleteHolidayEntry(id);
      setHolidays(holidays.filter(h => h.id !== id));
      showMessage('Holiday deleted');
    } catch (err) { showMessage(err.message, true); }
  };

  const handleSyncAll = async () => {
    if (!window.confirm(`Sync all holidays for ${filterYear} to all employee timesheets?`)) return;
    try {
      await syncHolidaysForYear(filterYear);
      showMessage(`Holidays synced for all employees for ${filterYear}`);
    } catch (err) { showMessage(err.message, true); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div>
      <div className="admin-form">
        <h3>{editingId ? 'Edit Holiday' : 'Add Holiday'}</h3>
        <div className="form-row">
          <label>Year Filter:</label>
          <select value={filterYear} onChange={(e) => setFilterYear(parseInt(e.target.value))}>
            {[2023, 2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <input
            type="date"
            value={form.holiday_date}
            onChange={(e) => setForm({ ...form, holiday_date: e.target.value })}
          />
          <input
            placeholder="Holiday Name"
            value={form.holiday_name}
            onChange={(e) => setForm({ ...form, holiday_name: e.target.value })}
          />
          <button onClick={handleSave} className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="btn-secondary">Cancel</button>}
          <button onClick={handleSyncAll} className="btn-sync" title="Sync holidays to all employee timesheets">
            Sync All Timesheets ({filterYear})
          </button>
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr><th>Date</th><th>Day</th><th>Name</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {holidays.map(item => (
            <tr key={item.id}>
              <td>{item.holiday_date}</td>
              <td>{formatDate(item.holiday_date)}</td>
              <td>{item.holiday_name}</td>
              <td>
                <button onClick={() => handleEdit(item)} className="btn-edit">Edit</button>
                <button onClick={() => handleDelete(item.id)} className="btn-delete-admin">Delete</button>
              </td>
            </tr>
          ))}
          {holidays.length === 0 && <tr><td colSpan="4" className="empty-cell">No holidays for {filterYear}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============ Backup Tab ============
function BackupTab({ showMessage }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const tables = [
    { key: 'employees', label: 'Employees', hasYear: false },
    { key: 'cost_centers', label: 'Cost Centers', hasYear: false },
    { key: 'customers', label: 'Customers', hasYear: false },
    { key: 'job_types', label: 'Job Types', hasYear: false },
    { key: 'job_ids', label: 'Job IDs', hasYear: false },
    { key: 'timesheet_lines', label: 'Timesheet Lines', hasYear: true },
    { key: 'timesheet_entries', label: 'Timesheet Entries', hasYear: true },
    { key: 'timesheet_status', label: 'Timesheet Status', hasYear: true },
    { key: 'vacations', label: 'Vacations', hasYear: true },
    { key: 'company_holidays', label: 'Company Holidays', hasYear: true }
  ];

  const handleExport = async (table) => {
    try {
      const data = await getBackupData(table.key, selectedYear);
      if (data.rows.length === 0) {
        showMessage(`No data found for ${table.label}${table.hasYear ? ` in ${selectedYear}` : ''}`, true);
        return;
      }

      const headers = data.headers;
      const csvRows = [
        headers.join(','),
        ...data.rows.map(row =>
          headers.map(h => {
            const val = row[h];
            if (val === null || val === undefined) return '';
            const str = String(val);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          }).join(',')
        )
      ];
      const csv = csvRows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${table.key}_${selectedYear}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showMessage(`${table.label} exported successfully`);
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>Backup Tables to CSV</h3>
        <div className="form-row">
          <label>Year:</label>
          <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {[2023, 2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <span className="backup-hint">Year filter applies to time-based tables only</span>
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr><th>Table</th><th>Year Filtered</th><th>Action</th></tr>
        </thead>
        <tbody>
          {tables.map(table => (
            <tr key={table.key}>
              <td>{table.label}</td>
              <td>{table.hasYear ? `${selectedYear}` : 'All data'}</td>
              <td>
                <button onClick={() => handleExport(table)} className="btn-export">
                  Export CSV
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============ Vacation Balance Tab ============
function VacationBalanceTab({ employees, showMessage }) {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const handleLoad = async () => {
    if (!selectedEmployee) return showMessage('Please select an employee', true);
    setLoading(true);
    try {
      const data = await getVacationBalance(selectedEmployee, selectedYear);
      setBalanceData(data);
    } catch (err) {
      showMessage(err.message, true);
      setBalanceData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="admin-form">
        <h3>Vacation Balance Report</h3>
        <div className="form-row">
          <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
            <option value="">Select Employee</option>
            {employees.filter(e => e.active).map(emp => (
              <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.username})</option>
            ))}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {[2023, 2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={handleLoad} className="btn-primary" style={{ width: 'auto' }} disabled={!selectedEmployee || loading}>
            {loading ? 'Loading...' : 'Load Balance'}
          </button>
        </div>
      </div>

      {balanceData && (
        <div className="summary-results">
          <div className="summary-header">
            <h3>{balanceData.employee.full_name} — {balanceData.year}</h3>
            <div className="summary-meta">
              <span>Start Date: <strong>{balanceData.employee.start_date}</strong></span>
              <span>Starting Vacation Days: <strong>{balanceData.startingDays}</strong></span>
              <span>Monthly Accrual: <strong>{balanceData.monthlyAccrual} days/month</strong></span>
              <span>Carry Over: <strong>{balanceData.carryOver} days</strong></span>
            </div>
          </div>
          <table className="admin-table summary-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Balance at Start (days)</th>
                <th>Accrued (days)</th>
                <th>Taken (days)</th>
                <th>Taken (hours)</th>
                <th>Balance at End (days)</th>
              </tr>
            </thead>
            <tbody>
              {balanceData.months.map(m => (
                <tr key={m.month}>
                  <td>{monthNames[m.month - 1]}</td>
                  <td>{m.balanceAtStart.toFixed(2)}</td>
                  <td>{m.accrued.toFixed(2)}</td>
                  <td>{m.taken > 0 ? m.taken.toFixed(2) : '—'}</td>
                  <td>{m.takenHours > 0 ? m.takenHours : '—'}</td>
                  <td className={m.balanceAtEnd < 0 ? 'negative' : ''}>{m.balanceAtEnd.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td></td>
                <td><strong>{balanceData.months.reduce((s, m) => s + m.accrued, 0).toFixed(2)}</strong></td>
                <td><strong>{balanceData.months.reduce((s, m) => s + m.taken, 0).toFixed(2)}</strong></td>
                <td><strong>{balanceData.months.reduce((s, m) => s + m.takenHours, 0)}</strong></td>
                <td><strong>{balanceData.months[11].balanceAtEnd.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ Edit Timesheets Tab ============
function EditTimesheetTab({ employees, showMessage }) {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [lines, setLines] = useState([]);
  const [refData, setRefData] = useState({ jobIds: [] });
  const [loading, setLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const daysInMonth = new Date(year, month, 0).getDate();

  useEffect(() => {
    getReferenceData().then(setRefData).catch(console.error);
  }, []);

  const loadTimesheet = async () => {
    if (!selectedEmployee) return showMessage('Please select an employee', true);
    setLoading(true);
    try {
      const data = await getAdminTimesheetLines(selectedEmployee, month, year);
      setLines(data);
    } catch (err) { showMessage(err.message, true); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedEmployee) loadTimesheet();
  }, [selectedEmployee, month, year]);

  const handleAddLine = async () => {
    if (!selectedJobId) return;
    try {
      const newLine = await addAdminTimesheetLine(selectedEmployee, parseInt(selectedJobId), month, year);
      setLines([...lines, newLine]);
      setSelectedJobId('');
    } catch (err) { showMessage(err.message, true); }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Delete this timesheet line?')) return;
    try {
      await deleteAdminTimesheetLine(lineId);
      setLines(lines.filter(l => l.id !== lineId));
    } catch (err) { showMessage(err.message, true); }
  };

  const handleHoursChange = async (lineId, day, value) => {
    const hours = parseFloat(value) || 0;
    try {
      await updateAdminTimesheetEntry(lineId, day, hours);
      setLines(lines.map(l => {
        if (l.id === lineId) {
          const entries = { ...l.entries };
          if (hours === 0) delete entries[day];
          else entries[day] = hours;
          return { ...l, entries };
        }
        return l;
      }));
    } catch (err) { showMessage(err.message, true); }
  };

  const getDayName = (day) => {
    const date = new Date(year, month - 1, day);
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
  };

  const isWeekend = (day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const getLineTotalHours = (line) => Object.values(line.entries).reduce((sum, h) => sum + h, 0);
  const getDayTotalHours = (day) => lines.reduce((sum, line) => sum + (line.entries[day] || 0), 0);

  const selectedEmpName = employees.find(e => e.id === parseInt(selectedEmployee))?.full_name || '';

  return (
    <div>
      <div className="admin-form">
        <h3>Edit Employee Timesheet</h3>
        <div className="form-row">
          <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
            <option value="">Select Employee</option>
            {employees.filter(e => e.active).map(emp => (
              <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.username})</option>
            ))}
          </select>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
            {monthNames.map((name, i) => (
              <option key={i + 1} value={i + 1}>{name}</option>
            ))}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
            {[2023, 2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        {selectedEmployee && (
          <div className="form-row" style={{ marginTop: '0.75rem' }}>
            <select value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)}>
              <option value="">Add Job ID...</option>
              {refData.jobIds && refData.jobIds.map(j => (
                <option key={j.id} value={j.id}>{j.job_id_code} - {j.description}</option>
              ))}
            </select>
            <button onClick={handleAddLine} className="btn-primary" style={{ width: 'auto' }} disabled={!selectedJobId}>Add Line</button>
          </div>
        )}
      </div>

      {selectedEmployee && !loading && lines.length > 0 && (
        <div className="admin-timesheet-info">
          <strong>{selectedEmpName}</strong> — {monthNames[month - 1]} {year}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading timesheet...</div>
      ) : selectedEmployee && lines.length > 0 ? (
        <div className="timesheet-grid-wrapper">
          <table className="timesheet-grid" role="grid" aria-label="Admin timesheet editor">
            <thead>
              <tr>
                <th className="sticky-col col-actions">Del</th>
                <th className="sticky-col col-jobid">Job ID</th>
                <th className="sticky-col col-desc">Description</th>
                <th className="sticky-col col-total">Total</th>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                  <th key={day} className={`day-header ${isWeekend(day) ? 'weekend' : ''}`}>
                    <div>{day}</div>
                    <div className="day-name">{getDayName(day)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map(line => (
                <tr key={line.id} className={line.job_id_code === 'G_VACATION' ? 'vacation-row' : line.job_id_code === 'G_HOLIDAY' ? 'holiday-row' : ''}>
                  <td className="sticky-col col-actions">
                    <button onClick={() => handleDeleteLine(line.id)} className="btn-delete" title="Delete line">×</button>
                  </td>
                  <td className="sticky-col col-jobid">{line.job_id_code}</td>
                  <td className="sticky-col col-desc" title={line.description}>{line.description}</td>
                  <td className="sticky-col col-total">{getLineTotalHours(line)}</td>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                    <td key={day} className={`day-cell ${isWeekend(day) ? 'weekend' : ''}`}>
                      <input
                        type="number"
                        min="0"
                        max="24"
                        step="0.5"
                        value={line.entries[day] || ''}
                        onChange={(e) => handleHoursChange(line.id, day, e.target.value)}
                        className="hours-input"
                        aria-label={`Hours for ${line.job_id_code} on day ${day}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="totals-row">
                <td className="sticky-col col-actions"></td>
                <td className="sticky-col col-jobid"><strong>Daily Total</strong></td>
                <td className="sticky-col col-desc"></td>
                <td className="sticky-col col-total"><strong>{lines.reduce((s, l) => s + getLineTotalHours(l), 0)}</strong></td>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                  <td key={day} className={`day-cell ${isWeekend(day) ? 'weekend' : ''}`}>
                    <strong>{getDayTotalHours(day) || ''}</strong>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : selectedEmployee && !loading ? (
        <div className="empty-state">
          <p>No timesheet lines for {selectedEmpName} in {monthNames[month - 1]} {year}.</p>
          <p>Use the dropdown above to add job IDs.</p>
        </div>
      ) : null}
    </div>
  );
}

export default AdminPage;
